import { Http } from '@pirates/not-morty-biz';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { PaymentProfile } from '@/store/user/user.model';
import {
  InvoiceDownload,
  InvoiceDownloadParams,
  RentalsParams,
  Rentals,
  SpecialPaymentLineItem,
  SpecialPayments,
  SpecialPaymentObject,
  CompanyInvoicesResponse,
  CompanyInvoice,
  GetCompanyInvoicesParams,
  InvoiceDownloadType,
  PaymentProfileOwner,
} from './rentals.model';
import { getTimezoneName } from '@/utils/date';

export class RentalsService {
  public async downloadInvoice(params: InvoiceDownloadParams): Promise<InvoiceDownload> {
    const response = await Http.getAxiosInstance().request<ArrayBuffer>({
      url: `/api/b2b/data-broker/v1/customer/companies/${params.refCompanyId}/invoices/${params.id}/${params.type}`,
      responseType: 'arraybuffer',
      method: 'GET',
    });

    return {
      /* eslint-disable no-useless-escape */
      filename:
        params.type === InvoiceDownloadType.pdf ? response!.headers['content-disposition']!.replace(/.*=\s*/, '').replace(/\"/g, '') : `Free2move_${params.id}.${params.type}`,
      data: response.data,
    };
  }

  public async getRentals({ id, paymentProfileId, from, to, customerId, withVlmData, size = 10000 }: RentalsParams) {
    const withVlmDataParam = withVlmData ? `&withVlmData=true` : '';
    const fromDate = format(from as Date, 'yyyy-MM');
    const toDate = format(to as Date, 'yyyy-MM');
    const customerIdParam = Number(customerId) ? `&customerId=${customerId}` : '';
    const timezoneName = getTimezoneName();

    return Http.get<Rentals>(
      `/api/b2b/data-broker/v1/customer/companies/${id}/rentals/${paymentProfileId}` +
        `?from=${fromDate}&to=${toDate}${customerIdParam}${withVlmDataParam}&size=${size}&timezone=${timezoneName}`,
    );
  }

  public async getAllRentals({ id, from, to, customerId, withVlmData }: RentalsParams) {
    const customerIdParam = Number(customerId) ? `&customerId=${customerId}` : '';
    const withVlmDataParam = withVlmData ? `&withVlmData=true` : '';
    return Http.get<Rentals>(`/api/b2b/data-broker/v1/customer/companies/${id}/rentals?from=${from}&to=${to}${customerIdParam}${withVlmDataParam}`);
  }

  public async getPaymentProfiles(id: string | number) {
    return Http.get<PaymentProfile[]>(`/api/b2b/data-broker/v1/customer/companies/${id}/payment-profiles`);
  }

  public async getPaymentProfileOwner(id: string | number) {
    return Http.get<PaymentProfileOwner>(`/api/b2b/data-broker/v1/customer/companies/${id}/payment-profile-owner`);
  }

  public async getUserPaymentProfiles(uuid: string) {
    return Http.get<PaymentProfile[]>(`/api/b2b/data-broker/v1/customer/payment-profiles/${uuid}`);
  }

  public async getSpecialPayments(refCompanyId: number, from: Date, to: Date, customerId?: number) {
    const [fromMonth, toMonth] = [startOfMonth(from).toISOString(), endOfMonth(to).toISOString()];

    const { specialPaymentsByCurrency } = await Http.get<SpecialPayments>(`/api/billing/company/${refCompanyId}/special-payments`, {
      params: {
        from: fromMonth,
        to: toMonth,
        driver_id: customerId ? customerId : undefined,
      },
    });

    return specialPaymentsByCurrency.reduce((a: SpecialPaymentLineItem[], b: SpecialPaymentObject) => {
      a.push(...b.items);
      return a;
    }, []);
  }

  public async getCorporateAgreement(paymentProfileId: number) {
    return Http.get<{ customer_group: { slug: string; name: string } }>(`/api/pricing/customer-group-tracker/b2b/v1/customer_groups`, {
      params: {
        payment_profile_id: paymentProfileId,
      },
    });
  }

  async getCompanyInvoices(params: GetCompanyInvoicesParams): Promise<string[]> {
    const { dateFrom, dateTo, refCompanyId, paymentProfileIds, page, size } = params;
    const paymentProfileId = paymentProfileIds.join(',');
    const { content } = await Http.get<CompanyInvoicesResponse>(`/api/b2b/data-broker/v1/customer/companies/${refCompanyId}/invoices`, {
      params: {
        paymentProfileId,
        dateFrom,
        dateTo,
        page,
        size, // ToDo currently we cant support paging due to UI complexity. However in future we ither support paging, or PB will allow paging to be optional
      },
    });

    if (content) {
      return content.map((invoice: CompanyInvoice) => invoice.invoiceNumber);
    }

    // API returns 204 with no response.body when no statements exist, hence we return an empty array
    return [];
  }
}

export const rentalsService = new RentalsService();
